import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';
import { useLocalStorageState } from 'shared_components/hooks/useLocalStorageState';

// Coarse local status, kept for the existing button/label logic and persisted across reloads.
type RunStatus =
  | 'not-started'
  | 'preparing'
  | 'running'
  | 'canceling'
  | 'cancelled'
  | 'finished'
  | 'failed'
  | null;

// High-resolution backend phase — the source of truth for the progress UI.
type RunPhase =
  | 'REQUESTED'
  | 'LAUNCHING'
  | 'STARTING'
  | 'RUNNING'
  | 'WAITING_FOR_SENSOR'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLING'
  | 'CANCELLED'
  | null;

type PrimaryDiscographyRunContextType = {
  status: RunStatus;
  phase: RunPhase;
  currentStep: number | null;
  totalSteps: number | null;
  currentStepLabel: string | null;
  isStalled: boolean;
  actionsDisabled: boolean;
  cancelRun: () => Promise<void>;
  startRun: () => Promise<void>;
  restartRun: () => Promise<void>;
};

const ACTIVE_STATUSES: RunStatus[] = ['preparing', 'running', 'canceling'];

// Map the backend phase (preferred) — or the legacy DagsterRunStatus — to the coarse status.
function deriveStatus(
  phase: string | null | undefined,
  status: string | null | undefined
): RunStatus {
  switch (phase) {
    case 'REQUESTED':
    case 'LAUNCHING':
    case 'STARTING':
      return 'preparing';
    case 'RUNNING':
    case 'WAITING_FOR_SENSOR':
      return 'running';
    case 'CANCELLING':
      return 'canceling';
    case 'CANCELLED':
      return 'cancelled';
    case 'SUCCEEDED':
      return 'finished';
    case 'FAILED':
      return 'failed';
  }
  switch (status) {
    case 'QUEUED':
    case 'NOT_STARTED':
    case 'MANAGED':
    case 'STARTING':
      return 'preparing';
    case 'STARTED':
      return 'running';
    case 'SUCCESS':
      return 'finished';
    case 'CANCELING':
      return 'canceling';
    case 'CANCELED':
      return 'cancelled';
    case 'FAILURE':
      return 'failed';
    default:
      return 'not-started';
  }
}

const PrimaryDiscographyRunContext = createContext<
  PrimaryDiscographyRunContextType | undefined
>(undefined);

const PrimaryDiscographyRunProviderImplementation = ({
  children,
  clientId,
}: {
  readonly children: React.ReactNode;
  readonly clientId: string;
}) => {
  // Guards against firing a second create/restart before the first request settles. The
  // backend is the real guarantee (it rejects a concurrent trigger with 409); this just
  // avoids a wasted round-trip and a flash of stale UI.
  const creatingRun = useRef(false);
  const [status, setStatus] = useLocalStorageState<RunStatus>(
    'primary_discography_run_status',
    null,
    { namespace: clientId }
  );
  const [phase, setPhase] = useState<RunPhase>(null);
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [totalSteps, setTotalSteps] = useState<number | null>(null);
  const [currentStepLabel, setCurrentStepLabel] = useState<string | null>(null);
  const [isStalled, setIsStalled] = useState<boolean>(false);

  const { adminApi: api } = useApi();

  const getLatestPipelineStatus = useCallback(async (clientId: string) => {
    return api.retrievePipelineRunStatus({ userId: clientId }).then((data) => {
      setPhase((data.phase ?? null) as RunPhase);
      setCurrentStep(data.currentStep ?? null);
      setTotalSteps(data.totalSteps ?? null);
      setCurrentStepLabel(data.currentStepLabel ?? null);
      setIsStalled(Boolean(data.isStalled));
      setStatus(deriveStatus(data.phase, data.status));
    });
  }, []);

  useEffect(() => {
    if (creatingRun.current) return;
    if (!ACTIVE_STATUSES.includes(status)) {
      getLatestPipelineStatus(clientId).catch((err) => {
        console.log(err);
        setStatus((state) =>
          !ACTIVE_STATUSES.includes(state) ? 'not-started' : state
        );
      });
    }
  }, [clientId, status]);

  useEffect(() => {
    if (creatingRun.current) return;
    if (!ACTIVE_STATUSES.includes(status)) return;

    let finalJobStatusFetchTimeout: ReturnType<typeof setTimeout> | null = null;

    const fetchFinalJobStatus = () => {
      getLatestPipelineStatus(clientId).catch((err) => {
        console.log(err);
        setStatus('not-started');
      });

      finalJobStatusFetchTimeout = setTimeout(() => {
        fetchFinalJobStatus();
      }, 3000);
    };

    finalJobStatusFetchTimeout = setTimeout(() => {
      fetchFinalJobStatus();
    }, 3000);

    return () => {
      if (finalJobStatusFetchTimeout) {
        clearTimeout(finalJobStatusFetchTimeout);
      }
    };
  }, [status, clientId]);

  useEffect(() => {
    if (creatingRun.current) return;
    getLatestPipelineStatus(clientId).catch((err) => {
      console.log(err);
      setStatus((state) =>
        !ACTIVE_STATUSES.includes(state) ? 'not-started' : state
      );
    });
  }, [clientId]);

  const startRun = useCallback(async () => {
    if (creatingRun.current) return;
    creatingRun.current = true;
    setStatus('preparing');
    setPhase('REQUESTED');
    setIsStalled(false);
    try {
      await api.createPipelineRunStatus({
        pipelineRunRequest: { userId: clientId },
      });
    } catch (err) {
      // 409 = a run is already active for this artist (e.g. another tab/admin). That is the
      // backend doing its job; just fall through and poll for the real state.
      console.log(err);
    } finally {
      creatingRun.current = false;
    }
    getLatestPipelineStatus(clientId).catch((err) => {
      console.log(err);
      setStatus((state) =>
        !ACTIVE_STATUSES.includes(state) ? 'not-started' : state
      );
    });
  }, [clientId]);

  const restartRun = useCallback(async () => {
    if (creatingRun.current) return;
    creatingRun.current = true;
    setStatus('preparing');
    setPhase('LAUNCHING');
    setIsStalled(false);
    try {
      await api.restartPrimaryDiscographyRunPipelineRunStatus({
        userId: clientId,
      });
    } catch (err) {
      console.log(err);
    } finally {
      creatingRun.current = false;
    }
    getLatestPipelineStatus(clientId).catch((err) => {
      console.log(err);
    });
  }, [clientId]);

  const cancelRun = useCallback(async () => {
    setStatus('canceling');
    setPhase('CANCELLING');
    setIsStalled(false);

    api
      .cancelPrimaryDiscographyRunPipelineRunStatus({ userId: clientId })
      .then(() =>
        getLatestPipelineStatus(clientId).catch((err) => {
          console.log(err);
          setStatus((state) =>
            !ACTIVE_STATUSES.includes(state) ? 'not-started' : state
          );
        })
      )
      .catch((ex) => {
        console.log(ex);
      });
  }, [clientId]);

  const actionsDisabled = useMemo<boolean>(() => {
    switch (status) {
      case 'not-started':
      case 'finished':
      case 'cancelled':
      case 'failed':
        return false;
      default:
        return true;
    }
  }, [status]);

  const value = useMemo(() => {
    return {
      status,
      phase,
      currentStep,
      totalSteps,
      currentStepLabel,
      isStalled,
      actionsDisabled,
      startRun,
      restartRun,
      cancelRun,
    };
  }, [
    status,
    phase,
    currentStep,
    totalSteps,
    currentStepLabel,
    isStalled,
    actionsDisabled,
    startRun,
    restartRun,
    cancelRun,
  ]);

  return (
    <PrimaryDiscographyRunContext.Provider value={value}>
      {children}
    </PrimaryDiscographyRunContext.Provider>
  );
};

export const PrimaryDiscographyRunProvider = (
  props: Omit<
    ComponentProps<typeof PrimaryDiscographyRunProviderImplementation>,
    'clientId'
  >
) => {
  const { clientId } = useClientId();
  if (!clientId) return null;

  return (
    <PrimaryDiscographyRunProviderImplementation
      {...props}
      clientId={clientId}
    />
  );
};

export const usePrimaryDiscographyRun = () => {
  const context = useContext(PrimaryDiscographyRunContext);

  if (!context)
    throw new Error('Cannot use PrimaryDiscographyRunContext without provider');

  return context;
};
